import { hasPlasticCard, isCred, isEfr, isJuniorMermaid } from '../../utils/cert'
import { getStockTypeIdByCertId } from '../../services/utilities'

const STANDARD_CARD_STR = '普通'
// 免费首卡锚点：服务端按目录 title 命中它才归零
// （EcardCommon.FREE_FIRST_CARD_STYLE_MARKER = "普通版"，2026-09-01 起锚点由 style_name 迁到 title）。
// 与上面的 STANDARD_CARD_STR **不是一个词、不可合并**：「普通」用于卡面 SKU 解析
// （getSkuInfo 区分普通实体卡/环保实体卡），是刻意的宽匹配；这里是资格判定的锚点，
// 必须与服务端逐字一致——用宽匹配会把服务端按目录价计费的行误判为免费（少扣 99）。
const FREE_FIRST_CARD_TITLE_MARKER = '普通版'
export const CARD_TYPE_PLASTIC = '实体卡'
const FREE_FIRST_CARD_REQUEST_TYPE = 'CARD_REQUEST'
const CARD_TYPE_E = '电子卡'
const STANDARD_CARD_IMAGE = 'https://img.yzcdn.cn/upload_files/2020/04/15/Fjjy-dI_w1MsK6U8PLO_3TNppFgB.jpg'
const STANDARD_CARD_IMAGE_2026 = require('../../assets/images/certs/cert_60years.jpg')

/**
 * 目录商品类型（ecard_goods.type 枚举）→ 中文标签。
 * 购物车响应契约改造（2026-09-02）后条目不再带 typeName（有赞 properties 透传），
 * 展示用标签由此映射（沿用购物车链路既有硬编码中文风格）；未识别枚举兜底原样回显。
 */
export const typeLabel = (type) => ({ STANDARD_PHYSICAL: CARD_TYPE_PLASTIC, ELECTRONIC: CARD_TYPE_E })[type] || type

const getCardOptions = (cardOptions) => {
  const is2026 = new Date().getFullYear() === 2026
  return cardOptions.reduce((res, cur) => {
    if (!res.find((o) => o.styleName === cur.styleName)) {
      // 默认展示蓝色标准卡面
      if (cur.styleName.indexOf(STANDARD_CARD_STR) >= 0 && res.length > 0) {
        cur.imageUrl = is2026 ? STANDARD_CARD_IMAGE_2026 : STANDARD_CARD_IMAGE
      }
      res.push(cur)
    }
    return res
  }, [])
}

export const getMinPriceOptions = (cardOptions) => {
  let obj = {}
  cardOptions.forEach((item) => {
    if (obj[item.styleName]) {
      obj[item.styleName].push(item)
    } else {
      obj[item.styleName] = [item]
    }
  })
  let arr2 = []
  for (let k in obj) {
    obj[k].sort((a, b) => {
      return a.price - b.price
    })
    arr2.push(obj[k][0])
  }
  return getCardOptions(arr2)
}

/**
 * 免费首卡单的运费金额：**固定规则价 20 元**，与服务端 `EcardSaleService.FREE_FIRST_CARD_EXPRESS_PRICE`
 * 同值。**不读目录 `EXPRESS_FEE.price`**：后端从不取该价（目录 SKU 只提供台账运费行的 itemNo/title，
 * 整行缺失时服务端照收 20 并合成「运费」行），照目录价算会在「目录价 ≠ 20」或「目录缺该行」时
 * 与 `orderPreview` / 实际下单金额不一致。
 */
const FREE_FIRST_CARD_EXPRESS_FEE = 20

/**
 * 运费（元）：本地预估（服务端 `orderPreview.expressPrice` 才是权威值，这里是购物车合计与试算占位）。
 * 运费口径（2026-09-16 起、2026-09-17 收紧）：普通实体卡与所有用券单一律包邮；**只有「整单没有
 * 付费实体卡」的免费首卡单**收运费（免费卡单独寄一次）——免费卡与付费实体卡同车时，付费卡价已含
 * 运费、两张卡一个快递寄出，故包邮。电子卡/增值权益是虚拟交付，不算付费实体卡。
 * 金额取 FREE_FIRST_CARD_EXPRESS_FEE（规则价），与确认订单弹窗、购物车底部总计、购物车页
 * 邮费展示同口径；服务端金额权威。
 *
 * @param {Array} goods ecard_goods 目录项（只用来判「哪些 itemNo 是实体卡」，不参与金额）
 * @param {Array<string>} itemNos 订单明细 itemNo 全集
 * @param {number} freeFirstCardCount 订单内免费首卡条目数（缺省 0 = 包邮）
 * @param {boolean} hasPaidPhysicalItem 订单内是否有付费实体卡行（缺省 false）
 * @returns {number} 运费（元）
 */
export const getPostageFromCatalog = (goods, itemNos, freeFirstCardCount = 0, hasPaidPhysicalItem = false) => {
  const catalog = goods || []
  const physicalItemNos = new Set(catalog.filter((g) => g.type === 'STANDARD_PHYSICAL').map((g) => g.itemNo))
  const hasPhysical = (itemNos || []).some((itemNo) => itemNo && physicalItemNos.has(itemNo))
  return hasPhysical && freeFirstCardCount > 0 && !hasPaidPhysicalItem ? FREE_FIRST_CARD_EXPRESS_FEE : 0
}

/**
 * 条目在免费首卡判定中的身份键（`ecardId|itemNo`）。同证书**同一卡面**的重复明细 itemNo 相同
 * → 与首行同键（服务端已由 collapseDuplicatePhysicalItems 折叠为一行）；同证书**不同卡面**
 * itemNo 不同 → 不同键（服务端按目录价计费）。
 * hasPaidPhysicalItem 与购物车行的「免费」标签共用，避免各自拼串漂移。
 */
export const freeFirstCardKey = (item) => `${item.ecardId}|${item.itemNo ?? item.skuId}`

/**
 * 本单被服务端归零的条目键集合（freeFirstCardItems 已按证书去重）：给「某一行是否免费」做 O(1) 判定。
 */
export const freeFirstCardKeys = (items, ecards, redeemedIds = []) =>
  new Set(freeFirstCardItems(items, ecards, redeemedIds).map(freeFirstCardKey))

/**
 * 订单内是否有「付费实体卡」行：实体卡（slim 条目的 type / rich 条目的 typeName）且**不在**
 * 免费首卡条目里（freeFirstCardItems 已按证书去重，同证书重复明细的第二行按目录价计 → 算付费）。
 * 电子卡/增值权益不算（虚拟交付，不承担实体卡的快递成本）。
 *
 * 服务端判据是「STANDARD_PHYSICAL 且**归零后的**行价 > 0」（EcardSaleService#hasPaidPhysicalItem）。
 * 前端**取不到归零后的行价**：购物车条目的 price 是目录价，免费首卡归零发生在下单时的
 * applyFreeFirstCardPricing，那行在购物车里仍是全额。故此处以「不在免费首卡集合里」作为
 * 「行价 == 0」的等价替身——两者一致的前提是 freeFirstCardItems 的资格谓词与服务端逐字对齐
 * （title 锚点见 FREE_FIRST_CARD_TITLE_MARKER、证书侧见 isCred + CARD_REQUEST + 已兑列表）。
 * 任一条件放宽，都会把服务端按目录价计费的行当成免费行，从而漏判付费实体卡 → 该收的 20 元运费收不到。
 */
export const hasPaidPhysicalItem = (items, ecards, redeemedIds = []) => {
  const freeKeys = freeFirstCardKeys(items, ecards, redeemedIds)
  return (items || []).some((item) => {
    if (!(item.type === 'STANDARD_PHYSICAL' || item.typeName === CARD_TYPE_PLASTIC)) return false
    return !freeKeys.has(freeFirstCardKey(item))
  })
}

/**
 * 购物车条目的下单 itemNo：服务端新契约（2026-09-02）条目恒带 itemNo 直接命中；
 * fallback 仅防御本地遗留旧形态条目（无 itemNo，按 skuId 从 cardOptions 有赞 SKU
 * 体系取卡面标识，与 BuyCards 立即购买下单逻辑一致）。
 */
export const resolveCartItemNo = (goods, cardOptions, item) =>
  item.itemNo ||
  getCertItemNo(goods, {
    cardStockId: (cardOptions || []).find((o) => o.skuId === item.skuId)?.cardStockId,
    isPlastic: item.typeName === CARD_TYPE_PLASTIC,
  })

export const notAvailable = (ecard, selectedImage, isPlastic, buyCred) => {
  if (
    (isEfr(ecard) && selectedImage.indexOf('普通版') >= 0 && !isPlastic) ||
    (isEfr(ecard) && selectedImage.indexOf('普通版') < 0) ||
    (selectedImage.indexOf('普通版') >= 0 && !hasPlasticCard(ecard.ecardName)) ||
    (isPlastic && ('Discover Scuba Diving' === ecard.ecardName || 'PADI Discover Mermaid' === ecard.ecardName)) ||
    (!isJuniorMermaid(ecard) && selectedImage.indexOf('青少年') === 0)
  ) {
    return '此证书不支持当前卡面类型组合，请选择其他卡面和类型'
  }
  if (isCred(ecard) && !buyCred) {
    return '请完成专业会员资格更新后，再申请专业会员卡'
  }
  return ''
}

export const getSkuInfo = (certOrCred, options, styleName, isPlastic) => {
  options = options.filter((o) => o.styleName === styleName)
  if (isPlastic) {
    // 实体卡不再按 ecardType 过滤（CHNA-2618：会员卡商品删除后实体卡 SKU 的 ecardType
    // 统一为 CERTIFICATION，按旧逻辑专业（CREDENTIAL）用户将匹配不到任何 SKU），
    // 普通版按 cardStockId + cardRequestType 匹配，其余款式取首条
    if (styleName.indexOf(STANDARD_CARD_STR) >= 0) {
      // 普通实体卡
      let stockId = getStockTypeIdByCertId(certOrCred.type, certOrCred.typeNumber)
      if ([8, 48, 77].indexOf(stockId) < 0) stockId = 1
      return options.find(
        (o) => Math.abs(o.cardStockId) === stockId && certOrCred.cardRequestType === o.cardRequestType
      )
    } else {
      // 环保实体卡
      return options[0]
    }
  } else {
    // 环保电子卡
    return options.filter((o) => o.typeName === CARD_TYPE_E)[0]
  }
}

/**
 * 从 ecard_goods 商品目录（GET /api/ecommerce/goods）匹配证书卡下单所需的 itemNo。
 * createEcardOrder 的 itemNo 必填且查 ecard_goods 唯一键；cardOptions 是有赞缓存体系、不含 itemNo，
 * 故证书明细按下单时选择的卡面（cardStockId）+ 卡类型（实体/电子）在目录中匹配 SKU 编码。
 *
 * @param {Array} goods ecard_goods 目录项（含 itemNo/type/cardStockId 等）
 * @param {{cardStockId: number|null, isPlastic: boolean}} sku 证书 SKU（cardOptions 体系）
 * @returns {string|null} 匹配的 itemNo；目录未配置该卡面时返回 null（调用方需拦截并提示）
 */
export const getCertItemNo = (goods, { cardStockId, isPlastic }) => {
  if (!goods || goods.length === 0 || !cardStockId) return null
  // 实体卡 STANDARD_PHYSICAL（MEMBER_CARD 已退役），电子卡 ELECTRONIC
  const types = isPlastic ? ['STANDARD_PHYSICAL'] : ['ELECTRONIC']
  const matched = goods.find((g) => g.cardStockId === cardStockId && types.includes(g.type))
  return matched ? matched.itemNo : null
}

/**
 * 免费首卡候选条目（购物车总计扣减/运费预估共用判定）：普通版实体卡且
 * 命中本人教练证书（isCred，专业资质体系——学生证书首张按 99 元收费）
 * cardRequestType=CARD_REQUEST，与服务端归零口径一致。
 * redeemedIds = 已占用/已兑免费首卡的证书 encodedId 列表
 * （GET /api/ecommerce/freeFirstCardRedeemed，WAIT_PAY 占用即计）——命中的证书
 * 不再计免费（服务端按目录价 99 收费），默认 [] 向后兼容。
 * 2026-09-17 起服务端取消「每单限 1 张」：一单可有多张免费卡（多张教练证书各兑一张），
 * 但**同一证书只算首条**（同一证书的后续明细服务端按目录价 99 计），故按 ecardId 去重。
 */
export const freeFirstCardItems = (items, ecards, redeemedIds = []) => {
  const countedEcars = new Set()
  return (items || []).filter((item) => {
    if (!(item.type === 'STANDARD_PHYSICAL' || item.typeName === CARD_TYPE_PLASTIC)) return false
    if (!(item.title || item.styleName || '').includes(FREE_FIRST_CARD_TITLE_MARKER)) return false
    if (!item.ecardId) return false
    if ((redeemedIds || []).includes(item.ecardId)) return false
    if (countedEcars.has(item.ecardId)) return false
    const cert = (ecards || []).find((ecard) => ecard.encodedId === item.ecardId)
    const matched = !!cert && isCred(cert) && cert.cardRequestType === FREE_FIRST_CARD_REQUEST_TYPE
    if (matched) countedEcars.add(item.ecardId)
    return matched
  })
}

/**
 * 免费首卡条目数（运费预估第三参：> 0 即收一次目录运费）。
 *
 * @param {Array} items 订单明细（结算弹窗 checkoutItems）
 * @param {Array} ecards 用户证书列表（含 encodedId/cardRequestType）
 * @param {Array<string>} redeemedIds 已兑免费首卡的证书 encodedId 列表
 * @returns {number} 免费首卡条目数（同证书多条只计 1）
 */
export const countFreeFirstCards = (items, ecards, redeemedIds = []) =>
  freeFirstCardItems(items, ecards, redeemedIds).length

/**
 * 免费首卡资格判定（CHNA-2618）：资格是每张证书维度的（服务端按证书的
 * cardRequestType 对普通版自动归零），且仅限教练证书（isCred，专业资质体系，
 * 学生证书首张按 99 元收费），只有全部已选证书都是教练证书 + CARD_REQUEST
 * 才整体按免费展示，避免补卡/学生证书/混合选择被标成免费（实际按 99 元收费）。
 *
 * @param {Array<string>} selectedCerts 已选证书 encodedId 列表
 * @param {Array} ecards 用户证书列表（含 encodedId/cardRequestType）
 * @param {Array<string>} redeemedIds 已兑免费首卡证书列表（默认 []，向后兼容）
 * @returns {boolean} 全部已选证书均为教练证书 CARD_REQUEST（至少选中 1 张）且未兑
 */
export const isAllFreeFirstCardCerts = (selectedCerts, ecards, redeemedIds = []) => {
  if (!selectedCerts || selectedCerts.length === 0) return false
  return selectedCerts.every((certId) => {
    if ((redeemedIds || []).includes(certId)) return false
    const cert = (ecards || []).find((ecard) => ecard.encodedId === certId)
    return !!cert && isCred(cert) && cert.cardRequestType === FREE_FIRST_CARD_REQUEST_TYPE
  })
}
